import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Send } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { AuditChip } from '@/components/audit/AuditChip';
import { useRunDigests } from '@/queries/digests';
import { digestRunRequestSchema, type DigestPeriod } from '@/schemas/admin/digests';
import { useApiError } from '@/lib/useApiError';

export const Route = createFileRoute('/_authed/digests')({
  component: DigestsScreen,
});

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function DigestsScreen() {
  const { t } = useTranslation('digests');
  const run = useRunDigests();
  const resolveError = useApiError();
  const [period, setPeriod] = React.useState<DigestPeriod>('daily');
  const [userId, setUserId] = React.useState('');
  const [error, setError] = React.useState<string | undefined>(undefined);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (userId && !UUID_REGEX.test(userId)) {
      setError('Invalid UUID');
      return;
    }
    setError(undefined);
    const candidate = {
      period,
      ...(userId ? { user_id: userId } : {}),
    };
    const parsed = digestRunRequestSchema.safeParse(candidate);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid input');
      return;
    }
    try {
      const result = await run.mutateAsync(parsed.data);
      if (result.sent > 0) {
        toast.success(t('form.saved', { count: result.sent }));
      } else {
        toast.message(t('form.skipped'));
      }
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    }
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
        <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">{t('subtitle')}</p>
      </header>
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label>{t('form.period')}</Label>
              <div role="radiogroup" className="flex gap-2">
                {(['daily', 'weekly'] as const).map((p) => (
                  <label
                    key={p}
                    className={`cursor-pointer rounded-[var(--radius-pill)] border border-[color:var(--color-border)] px-3 py-1 text-xs font-semibold ${
                      period === p ? 'bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      className="sr-only"
                      checked={period === p}
                      onChange={() => setPeriod(p)}
                      name="digest-period"
                    />
                    {t(`form.${p}`)}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label htmlFor="digest-user">{t('form.userId')}</Label>
              <Input
                id="digest-user"
                dir="ltr"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder={t('form.userIdPlaceholder')}
                className="font-mono"
                aria-invalid={Boolean(error)}
              />
              <p className="mt-1 text-xs text-[color:var(--color-fg-muted)]">{t('form.userIdHint')}</p>
              {error ? (
                <p role="alert" className="mt-1 text-xs text-[color:var(--color-danger)]">
                  {error}
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
              <AuditChip />
              <Button type="submit" loading={run.isPending}>
                <Send className="size-4" aria-hidden />
                {t('form.submit')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
